import { ChatBubbleOvalLeftIcon, HeartIcon, ShareIcon } from '@heroicons/react/24/outline'
import classnames from 'classnames'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'

import { IMAGE_SIZES, Shield } from 'images'
import {
  actions,
  getLocalGoals,
  getMatchDate,
  getVisitorGoals,
  isMatchApproved,
  isMatchDeferred,
  isMatchEnded,
  isMatchPlaying,
  isMatchWaiting,
  selectors,
} from 'match'
import { EventType } from 'sports'
import System, { Feature } from 'system'
import { ProgressBar, SimpleButton, Tooltip } from 'ui'

import Share from 'components/site/Share'
import HeaderActions from 'components/sports/soccer/matches/Header/Actions'
import Time from 'components/sports/soccer/matches/Header/Time'

import type { FC } from 'react'

interface HeaderProps {
  id: string

  className?: string
}

const Header: FC<HeaderProps> = ({ id, className }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const store = useSelector(selectors.match)

  const match = store.data[0]
  const isLoading = store.loading || store.key !== id || !match

  useEffect(() => {
    if (store.key !== id) {
      dispatch(actions.matchFetch({ id }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  if (isLoading) {
    return (
      <div className={classnames('MatchHeader bg-white pt-2 rounded', className)}>
        <ProgressBar value={100} />
      </div>
    )
  }

  const localRedCards = match.events.filter(
    (evt) =>
      evt.team.id === match.local.id && (evt.type === EventType.CARD_RED || evt.type === EventType.CARD_RED_DIRECT),
  ).length
  const localYellowCards = match.events.filter(
    (evt) => evt.team.id === match.local.id && evt.type === EventType.CARD_YELLOW,
  ).length
  const localBlueCards = match.events.filter(
    (evt) => evt.team.id === match.local.id && evt.type === EventType.CARD_BLUE,
  ).length
  const localGreenCards = match.events.filter(
    (evt) => evt.team.id === match.local.id && evt.type === EventType.CARD_GREEN,
  ).length

  const visitorRedCards = match.events.filter(
    (evt) =>
      evt.team.id === match.visitor.id && (evt.type === EventType.CARD_RED || evt.type === EventType.CARD_RED_DIRECT),
  ).length
  const visitorYellowCards = match.events.filter(
    (evt) => evt.team.id === match.visitor.id && evt.type === EventType.CARD_YELLOW,
  ).length
  const visitorBlueCards = match.events.filter(
    (evt) => evt.team.id === match.visitor.id && evt.type === EventType.CARD_BLUE,
  ).length
  const visitorGreenCards = match.events.filter(
    (evt) => evt.team.id === match.visitor.id && evt.type === EventType.CARD_GREEN,
  ).length
  return (
    <div className={classnames('MatchHeader bg-greyLightest', className)}>
      <div className="p-2 first-letter:uppercase font-bold truncate bg-white text-center">{getMatchDate(match)}</div>
      <div className="relative">
        <div className="absolute top-0 h-14 w-full bg-white rounded-b-md" />
        <div className="flex flex-col items-center relative">
          <div className="flex items-start">
            <div className="flex flex-col gap-0.5 mb-2 self-center items-center justify-center rounded-md border-4 border-white bg-white w-6">
              {localRedCards > 0 && (
                <div className="rounded bg-red p-1 font-bold leading-4 text-white">{localRedCards}</div>
              )}
              {localYellowCards > 0 && (
                <div className="rounded bg-yellow p-1 font-bold leading-4 text-white">{localYellowCards}</div>
              )}
              {localBlueCards > 0 && (
                <div className="rounded bg-blue p-1 font-bold leading-4 text-white">{localBlueCards}</div>
              )}
              {localGreenCards < 0 && (
                <div className="rounded bg-green p-1 font-bold leading-4 text-white">{localGreenCards}</div>
              )}
            </div>
            <Link className="!text-text text-center" to={`/sports/soccer/team/${match.local.id}`}>
              <div className="inline-block rounded-full border-4 border-greyLightest">
                <Shield
                  className="border-1 border-white bg-greyLightest"
                  size="5.75em"
                  src={match.local.avatar}
                  srcImageSize={IMAGE_SIZES.FIT_100_100}
                />
              </div>
              <div className="font-bold truncate max-w-24 text-center leading-4 text-xl">
                {match.local.nickname}
                <div aria-hidden className="h-px overflow-hidden opacity-0">
                  {match.visitor.nickname}
                </div>
              </div>
            </Link>
            <div className="bg-white rounded-2xl px-1.5 pb-1.5 mb-6 self-center">
              {match.is_both_lose ? (
                <div className={classnames('text-center', isMatchApproved(match) ? 'text-black' : 'text-grey')}>
                  Pierden
                  <br />
                  ambos
                </div>
              ) : (
                <div
                  className={classnames(
                    'flex flex-col gap-0.5',
                    isMatchApproved(match) && 'text-black',
                    isMatchDeferred(match) && 'text-grey',
                    !isMatchApproved(match) && isMatchEnded(match) && 'text-green',
                    isMatchPlaying(match) && 'text-yellow',
                    isMatchWaiting(match) && 'text-grey',
                  )}
                >
                  <div className="text-center text-sm">
                    {isMatchApproved(match) && 'Aprobado'}
                    {isMatchDeferred(match) && 'Pospuesto'}
                    {!isMatchApproved(match) && isMatchEnded(match) && 'Sin validar'}
                    {isMatchPlaying(match) && 'En juego'}
                    {isMatchWaiting(match) && 'En espera'}
                  </div>
                  <div className="flex gap-0.5 justify-center items-center font-bold text-2xl leading-4">
                    <div className="text-right">
                      {getLocalGoals(match)}
                      <div aria-hidden className="h-px overflow-hidden opacity-0">
                        {getVisitorGoals(match)}
                      </div>
                    </div>
                    <div>~</div>
                    <div className="text-left">
                      {getVisitorGoals(match)}
                      <div aria-hidden className="h-px overflow-hidden opacity-0">
                        {getLocalGoals(match)}
                      </div>
                    </div>
                  </div>
                  {(!!match.local_definition_goals || !!match.visitor_definition_goals) && (
                    <div className="flex gap-0.5 justify-center items-center font-bold">
                      <div className="text-right">
                        ({match.local_definition_goals})
                        <div aria-hidden className="h-px overflow-hidden opacity-0">
                          ({match.visitor_definition_goals})
                        </div>
                      </div>
                      <div>~</div>
                      <div className="text-left">
                        ({match.visitor_definition_goals})
                        <div aria-hidden className="h-px overflow-hidden opacity-0">
                          ({match.local_definition_goals})
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <Link className="!text-text text-center" to={`/sports/soccer/team/${match.visitor.id}`}>
              <div className="inline-block rounded-full border-4 border-greyLightest">
                <Shield
                  className="border-1 border-white bg-greyLightest"
                  size="5.75em"
                  src={match.visitor.avatar}
                  srcImageSize={IMAGE_SIZES.FIT_100_100}
                />
              </div>
              <div className="font-bold truncate max-w-24 text-center leading-4 text-xl">
                {match.visitor.nickname}
                <div aria-hidden className="h-px overflow-hidden opacity-0">
                  {match.local.nickname}
                </div>
              </div>
            </Link>
            <div className="flex flex-col gap-0.5 mb-2 self-center items-center justify-center rounded-md border-4 border-white bg-white w-6">
              {visitorRedCards > 0 && (
                <div className="rounded bg-red p-1 font-bold leading-4 text-white">{visitorRedCards}</div>
              )}
              {visitorYellowCards > 0 && (
                <div className="rounded bg-yellow p-1 font-bold leading-4 text-white">{visitorYellowCards}</div>
              )}
              {visitorBlueCards > 0 && (
                <div className="rounded bg-blue p-1 font-bold leading-4 text-white">{visitorBlueCards}</div>
              )}
              {visitorGreenCards < 0 && (
                <div className="rounded bg-green p-1 font-bold leading-4 text-white">{visitorGreenCards}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="pt-2 flex gap-2 justify-center">
        <div className="flex items-center gap-1 pt-1 px-2 bg-white rounded-t-xl w-auto *:text-inherit *:py-1 *:px-1.5">
          <HeaderActions className="*:static *:top-auto *:z-auto" />
          {isMatchPlaying(match) && (
            <div className="*:font-bold *:text-lg">
              <Time isRunning startedAt={match.started_at || match.matched_at || 0} />
            </div>
          )}
          {!Feature.isEnabled('ff_match_like') && (
            <SimpleButton aria-label="Me gusta este encuentro">
              <HeartIcon aria-hidden="true" className="size-5" strokeWidth="2" />
            </SimpleButton>
          )}
          <SimpleButton
            aria-label="Comentarios del encuentro"
            onClick={() => navigate(`/sports/soccer/match/${match.id}/feed`)}
          >
            <ChatBubbleOvalLeftIcon aria-hidden="true" className="size-5" strokeWidth="2" />
          </SimpleButton>
          <Tooltip
            on={['click']}
            tooltip={
              <Share
                className="text-xl"
                title={[
                  match.local.name,
                  'vs',
                  match.visitor.name,
                  '-',
                  match.stage.tournament.tournament.name,
                  '-',
                  match.stage.name,
                  '-',
                  match.round.name,
                ].join(' ')}
                unique={[
                  getLocalGoals(match),
                  match.local_definition_goals,
                  match.visitor_definition_goals,
                  getVisitorGoals(match),
                ].join('-')}
                url={`${System.services}/ssm/${match.id}`}
              />
            }
          >
            <SimpleButton aria-label="Compartir este encuentro">
              <ShareIcon aria-hidden="true" className="size-5" strokeWidth="2" />
            </SimpleButton>
          </Tooltip>
        </div>
      </div>
      <div className="h-4 w-full bg-white rounded-t-md" />
    </div>
  )
}

export default Header
